import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Fields,
    HorizontalFlex,
    Input,
    PadContainer,
    Title,
} from 'smartsuite-ui';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import EditorFilters from './EditorFilters';
import {
    areTimeAndPrecisionFieldsValid,
    isRequiredFiltersHandled,
} from '../shared/filtering/filtersModalUtils';
import EditorTimeAndPrecisionSelector from './EditorTimeAndPrecisionSelector';
import { useLabelContext } from '../shared/hooks/labelContext';
import DashboardContext from '../shared/hooks/dashboardContext';
import { getDashboardViewKey } from './customDashboardUtils';
import { getSelectOptionsForApiKeys } from '../shared/utilities/selectOptionsUtils';

function getNeededSelectOptionKeys(selectOptions, availableFilters) {
    const selectOptionsApiKeys = selectOptions.map((x) => x.fieldName);
    return map(availableFilters, 'apiKey').filter(
        (apiKey) => apiKey && !selectOptionsApiKeys.includes(apiKey)
    );
}

// eslint-disable-next-line complexity
export default function WidgetEditor(props) {
    const {
        deleteWidget,
        incorrectlyConfiguredDashboards,
        selectOptions,
        selectedWidget,
        setErrorWidgetIds,
        setIncorrectlyConfiguredDashboards,
        setSelectOptions,
    } = props;

    const { dashboardLabels, interpolateString } = useLabelContext();
    const { uriConstants } = useContext(DashboardContext);
    const [isLoadingSelectOptions, setIsLoadingSelectOptions] = useState(true);
    const { id, stateData, staticData } = selectedWidget;

    const dashboardViewKey = getDashboardViewKey(
        staticData.dashboardKey,
        staticData.viewKey
    );

    // eslint-disable-next-line complexity
    const isWidgetStateInvalid = useCallback(() => {
        if (!staticData) {
            return false;
        }
        const isTitleInvalid = !stateData.title;
        let areRequiredFiltersHandled = true;

        try {
            areRequiredFiltersHandled = isRequiredFiltersHandled(
                staticData.requiredFilterKeys,
                stateData.filterValues,
                staticData.availableFilters,
                selectOptions
            );
            setIncorrectlyConfiguredDashboards((prevState) => ({
                ...prevState,
                [dashboardViewKey]: undefined,
            }));
        } catch (error) {
            setIncorrectlyConfiguredDashboards((prevState) => ({
                ...prevState,
                [dashboardViewKey]: error.message,
            }));
            areRequiredFiltersHandled = false;
        }

        const widgetTimeFilterInvalid =
            !stateData.useDashboardTimeRange &&
            !areTimeAndPrecisionFieldsValid(
                stateData.timeAndPrecision,
                staticData.timeAndPrecisionRangeDomains,
                staticData.showPrecisionConfig,
                interpolateString,
                dashboardLabels
            );

        return (
            isTitleInvalid ||
            !areRequiredFiltersHandled ||
            widgetTimeFilterInvalid
        );
    }, [
        dashboardViewKey,
        interpolateString,
        dashboardLabels,
        selectOptions,
        setIncorrectlyConfiguredDashboards,
        stateData,
        staticData,
    ]);

    // eslint-disable-next-line complexity
    useEffect(() => {
        if (!isLoadingSelectOptions) {
            if (isWidgetStateInvalid()) {
                setErrorWidgetIds((prevState) => prevState.concat(id));
            } else {
                setErrorWidgetIds((prevState) =>
                    prevState.filter((x) => x !== id)
                );
            }
        }
    }, [id, isLoadingSelectOptions, isWidgetStateInvalid, setErrorWidgetIds]);

    useEffect(() => {
        let isMounted = true;

        // eslint-disable-next-line complexity
        async function initialize() {
            if (isEmpty(staticData)) {
                return;
            }

            const neededFilterApiKeys = getNeededSelectOptionKeys(
                selectOptions,
                staticData.availableFilters
            );

            if (neededFilterApiKeys?.length) {
                setIsLoadingSelectOptions(true);

                const newSelectOptions = await getSelectOptionsForApiKeys(
                    uriConstants.selectOptionsUri,
                    neededFilterApiKeys,
                    () => {}
                );

                setSelectOptions((prevState) => [
                    ...prevState,
                    ...newSelectOptions,
                ]);
            }

            if (isMounted) {
                setIsLoadingSelectOptions(false);
            }
        }

        initialize();

        return () => {
            isMounted = false;
        };
    }, [
        deleteWidget,
        selectOptions,
        setIncorrectlyConfiguredDashboards,
        setSelectOptions,
        staticData,
        uriConstants.selectOptionsUri,
    ]);

    return (
        <PadContainer cssClassModifiers={['pad-medium']}>
            <HorizontalFlex cssClassModifiers={['space-between']}>
                <Title cssClassModifiers={['size-small']}>
                    {dashboardLabels.details}
                </Title>
            </HorizontalFlex>
            <Fields cssClassModifiers={['no-background', 'column']}>
                <Input
                    label={dashboardLabels.title}
                    value={stateData.title ?? ''}
                    isValid={!!stateData.title}
                    onChange={(event) =>
                        props.updateWidget({ title: event.target.value })
                    }
                />
            </Fields>
            <PadContainer cssClassModifiers={['pad-medium']}>
                <HorizontalFlex cssClassModifiers={['space-between']}>
                    <Title cssClassModifiers={['size-small']}>
                        {dashboardLabels.widgetFilters}
                    </Title>
                    <div className="editor-drawer__required-label">
                        {`*${dashboardLabels.required}`}
                    </div>
                </HorizontalFlex>
            </PadContainer>
            <hr className="divider" />
            <EditorTimeAndPrecisionSelector
                isGauge={staticData.isGauge}
                dashboardTimeAndPrecision={props.dashboardTimeAndPrecision}
                saveTimeAndPrecision={(timeAndPrecision) =>
                    props.updateWidget({ timeAndPrecision })
                }
                setUseDashboardTimeRange={(checked) =>
                    props.updateWidget({ useDashboardTimeRange: checked })
                }
                showPrecisionConfig={staticData.showPrecisionConfig || false}
                showTimeAndPrecision={true}
                timeAndPrecision={stateData.timeAndPrecision}
                timeAndPrecisionRangeDomains={
                    staticData.timeAndPrecisionRangeDomains
                }
                useDashboardTimeRange={stateData.useDashboardTimeRange}
            />
            <EditorFilters
                availableFilters={staticData.availableFilters}
                filterValues={stateData.filterValues || {}}
                isLoadingSelectOptions={isLoadingSelectOptions}
                showRequiredFilterNoSelectOptionsError={
                    !!incorrectlyConfiguredDashboards[dashboardViewKey]
                }
                requiredFilterKeys={staticData.requiredFilterKeys || []}
                saveFilters={(filterValues) =>
                    props.updateWidget({ filterValues })
                }
                selectOptions={props.selectOptions}
                setSelectOptions={props.setSelectOptions}
            />
        </PadContainer>
    );
}

WidgetEditor.propTypes = {
    dashboardTimeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    deleteWidget: PropTypes.func.isRequired,
    incorrectlyConfiguredDashboards: PropTypes.instanceOf(Object).isRequired,
    selectOptions: PropTypes.instanceOf(Array).isRequired,
    selectedWidget: PropTypes.instanceOf(Object).isRequired,
    setErrorWidgetIds: PropTypes.func.isRequired,
    setSelectOptions: PropTypes.func.isRequired,
    updateWidget: PropTypes.func.isRequired,
    setIncorrectlyConfiguredDashboards: PropTypes.func.isRequired,
};
